import { useEffect } from 'react';
import { IOffer, ImportSource } from 'types';
import { Loading } from '../../atoms/loading';
import { MessageInbox } from '../../molecules/messageInbox';
import Footer from '../../molecules/Footer';
import { useSession } from 'shared-components/authentication';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { logout } from '../../../store/auth/actions';
import { getStudentMessages, markMessagesRead, saveMessage } from '../../../store/student/actions';
import { getNavigationUrls } from '../../../store/config/actions';
import { filterMessagesByType } from '../../../utilities/filterMessages';
import { getImportOffer } from '../../../utilities/helpers';
import { isStudentWithAgent } from '../../../utilities/pages';

const PageWrapper = ({ children, hideFooter }) => {
  const student = useSelector(({ student }: ApplicationState) => student);
  const studentNumber = useSelector(({ student }: ApplicationState) => student.core.student_number);
  const messages = useSelector(({ student }: ApplicationState) => student.messages);
  const hasImmigrationProviders = useSelector(
    ({ student }: ApplicationState) => !!student.core.immigrationProviders?.length
  );
  const visaAssistance = useSelector(({ student }: ApplicationState) => student.offer.visaSupport || false);
  const isSamlEnabled = useSelector(({ config }: ApplicationState) => config.isSamlEnabled);
  const studentAutomatedChatReply = useSelector(({ config }: ApplicationState) => config.studentAutomatedChatReply);
  const isLoading = useSelector(({ ui }: ApplicationState) => ui.isLoading);
  const apiCallsInProgress = useSelector(({ data }: ApplicationState) => data.apiCallsInProgress);
  const userName = useSelector(({ student }: ApplicationState) => student.personalInfo.givenName || '');

  const isAgent = isStudentWithAgent(
    student.personalInfo,
    getImportOffer(student.importData, student.offer.intakeId, student.offer.offerId),
    student.offer as IOffer
  );
  const hasAgentMessages = filterMessagesByType(student.messages, 'agent').length > 0;
  const hasSecureMessages = filterMessagesByType(student.messages, 'secure').length > 0;

  const session = useSession();

  const dispatch = useDispatch();
  const onSubmitMessage = message => dispatch(saveMessage(message));
  const markMessagesReadProp = messageIds => dispatch(markMessagesRead(messageIds));

  // Get navigation URLs from Redux store
  const navigationUrls = useSelector(({ config }: ApplicationState) => config.navigationUrls);
  const importData = useSelector(({ student }: ApplicationState) => student.importData);
  const university = useSelector(({ student }: ApplicationState) => student.core?.university);

  // Check if student has Apply access
  const hasApplyAccess = importData?.source === ImportSource.APPLY || !!importData?.applicantId;

  useEffect(() => {
    if (university && hasApplyAccess) {
      dispatch(getNavigationUrls(university));
    }
  }, [university, hasApplyAccess, dispatch]);

  const onLogout = async () => {
    await session.onLogout();
    dispatch(logout());
  };

  function onLoadMessages() {
    dispatch(getStudentMessages());
  }

  return (
    <>
      <Loading show={isLoading} apiCallsInProgress={apiCallsInProgress} />
      {children}
      <MessageInbox
        messages={messages}
        onSubmitMessage={onSubmitMessage}
        markMessagesRead={markMessagesReadProp}
        userName={userName}
        studentAutomatedChatReply={studentAutomatedChatReply}
        visaAssistance={visaAssistance}
        isAgent={isAgent}
        hasAgentMessages={hasAgentMessages}
        hasSecureMessages={hasSecureMessages}
        hasImmigrationProviders={hasImmigrationProviders}
        isSamlEnabled={isSamlEnabled}
        onLoadMessages={onLoadMessages}
      />
      {hideFooter ? null : (
        <Footer
          onLogout={onLogout}
          student_number={studentNumber}
          applyUrl={importData?.applicantId ? navigationUrls?.apply : null}
          hasApplyAccess={hasApplyAccess}
        />
      )}
    </>
  );
};

export default PageWrapper;
